/* @flow */
import Backbone from "backbone";
import Link from "nexia_mobile/framework/link";
import Rel from "nexia_mobile/framework/rel";
import BaseModel from "nexia_mobile/models/base_model";
import Backstage from "nexia_mobile/backstage";
import Url from "nexia_mobile/url";
import type { Url as UrlType } from "nexia_mobile/api/types";
import type { PromiseWithAbort } from "nexia_mobile/PromiseWithAbort";
import { createPromiseWithAbort } from "nexia_mobile/PromiseWithAbort";

let _hrefToResourceMap: { [key: UrlType]: Backbone.Model } = {};

export const ResourceMap = {
  findByHref(href: UrlType) {
    return _hrefToResourceMap[href];
  },

  clearAll() {
    _hrefToResourceMap = {};
  },
};

export type Resource = Object;

export type FetchOptions = {};

export default {
  parseLinks(attrs: Object): Object {
    let result = {}; // attributes to set() on model
    let linkMap = attrs._links || {};

    for (let key of Object.keys(Rel)) {
      let handlerName = Rel[key];
      let handler = this[`parseLinks${handlerName}`];

      // do we have a handler method for this link rel?
      if (typeof handler == "function") {
        // build an array of Link objects and call the handler with it
        let links = linkMap[key] || [];
        if (!_.isArray(links)) {
          links = [links];
        }
        links = _.map(links, (link) => new Link(link));
        // merge the handler's results into our output
        let handlerResult = handler.call(this, links);
        if (handlerResult instanceof Backbone.Model) {
          handlerResult = null;
        }
        if (handlerResult != null) {
          _.extend(result, handlerResult);
        }
      }
    }

    return result;
  },

  hasUrl(): boolean {
    try {
      return Boolean(this.getUrl());
    } catch (error) {
      return false;
    }
  },

  isNew(): boolean {
    return !this.hasUrl();
  },

  setUrl(href: UrlType) {
    return (this.id = href);
  },

  getUrlParams() {
    return null;
  },

  setUrlParams(params: Object) {
    return (this._resourceUrlParams = params);
  },

  getUrl(): ?UrlType {
    if (this._resourceUrlParams) {
      return Url.expandTemplate(this.id, this._resourceUrlParams);
    } else if (this.id) {
      return this.id;
    } else {
      return undefined;
    }
  },

  url(): UrlType {
    return this.getUrl();
  },

  setAsEphemeral(): void {
    this._ephemeral = true;
  },

  isEphemeral(): boolean {
    return Boolean(this._ephemeral || this.constructor.isEphemeral);
  },

  fetchUrlWithResourceClone(
    url: string,
    options: FetchOptions
  ): Promise<Resource> {
    let resourceClone = new this.constructor();
    resourceClone.setUrl(url);
    resourceClone.setAsEphemeral();
    resourceClone.clonedResource = this;
    return resourceClone.fetch(options).then(() => resourceClone);
  },

  parseLinksSelf(links: Array<Link>): Object {
    let link = _.first(links);
    if (!link) {
      return {};
    }

    if (!this.isEphemeral()) {
      _hrefToResourceMap[link.href] = this;
    }

    this.id = link.href; // we only need this for collections,
    // because they won't ever have the following attributes
    // set() on them.
    return { id: link.href };
  },

  maybeParseChildLinkForCollection(
    collection: Backbone.Collection<*>,
    link: Link
  ) {
    // Always parse if there is no collection in the ResourceMap, so that parseLinksSelf
    // puts the collection in the map. If the server (and links only come from the server)
    // has not sent an array of items, don't parse the data because it's only a stub.
    const data = link.getData();
    const resourceHref =
      link.href || (data._links && data._links.self) || data.id;
    if (!ResourceMap.findByHref(resourceHref) || data.items instanceof Array) {
      collection.set(data, { parse: true });
    }
  },

  parseLinksDetails(links: Array<Link>): Object {
    let link = _.first(links);

    return link ? { _detailsLinkHref: link.href } : {};
  },

  hasDetailsLink() {
    return Boolean(this.get("_detailsLinkHref"));
  },

  // Fetch the resource and also its details href, if it has one. This will only
  // fire one change event.
  fetchWithDetails(options: FetchOptions): PromiseWithAbort<void> {
    // If we don't have a details href, just perform a normal fetch, and the
    // changes will be applied atomically.
    if (!this.hasDetailsLink()) {
      return this.fetch(options);
    }

    // Fetch with a dummy model so that we can apply the set with the normal
    // attributes and the set with the details attributes as one atomic change.
    let normalDummy = new BaseModel();
    normalDummy.setUrl(this.url());
    normalDummy.setAsEphemeral();
    const normalFetch = normalDummy.fetch(options);

    let detailsDummy = new BaseModel();
    detailsDummy.setUrl(this.get("_detailsLinkHref"));
    detailsDummy.setAsEphemeral();
    const detailsFetch = detailsDummy.fetch(options);

    const combinedFetch = Promise.all([normalFetch, detailsFetch]).then(
      ([normalResponse, detailsResponse]) => {
        // Make sure to apply details attributes *after* applying the attributes
        // from a normal fetch, because otherwise, as a result of the model response
        // being parsed and applied after the details response, we might remove some
        // of the properties that were in the details response.
        const combinedResponse = {
          ...normalResponse,
          result: {
            ...normalResponse.result,
            ...detailsResponse.result,
          },
          // Ignore error/success from detailsResponse
        };

        this.set(this.parse(combinedResponse));
      }
    );

    const abort = () => {
      if (normalFetch.abort != null) {
        normalFetch.abort();
      }
      if (detailsFetch.abort != null) {
        detailsFetch.abort();
      }
    };

    return createPromiseWithAbort(combinedFetch, abort);
  },

  fetchDetails(options: FetchOptions): PromiseWithAbort<void> {
    if (!this.hasDetailsLink()) {
      throw new Error(
        "Attempted to fetch details for a resource that had no details link"
      );
    }

    let dummyModel = new BaseModel();
    dummyModel.setUrl(this.get("_detailsLinkHref"));
    dummyModel.setAsEphemeral();
    return dummyModel.fetch(options).done(() => {
      this.set(dummyModel.attributes);
    });
  },

  parseLinksPendingRequest(links: Array<Link>): ?Object {
    const link = links[0];
    if (link != null) {
      this._pendingRequestLink = link;
    } else {
      this._pendingRequestLink = null;
    }
  },

  hasPendingRequestLink(): boolean {
    return Boolean(this._pendingRequestLink);
  },

  fetchPendingRequestData(): Promise<Object> {
    if (this._fetchingPendingRequestDataPromise != null) {
      return this._fetchingPendingRequestDataPromise;
    }

    if (this._pendingRequestBackstage != null) {
      this._pendingRequestBackstage.stopPolling();
    }

    const link = this._pendingRequestLink;
    if (link == null) {
      return Promise.resolve({});
    }

    this._fetchingPendingRequestDataPromise = new Promise((resolve, reject) => {
      this._pendingRequestBackstage = new Backstage(link.polling_path, {
        success: (result) => {
          resolve(result.options);
        },
        error: (errors) => {
          reject(
            new Error(
              `fetchPendingRequestData failed: ${errors
                .map((err) => err.errorMessage)
                .join(", ")}`
            )
          );
        },
        maxRequestsCount: 10,
      });
      this._pendingRequestBackstage.startPolling();
    })
      .then((result) => {
        this._fetchingPendingRequestDataPromise = null;
        return result;
      })
      .catch((err) => {
        this._fetchingPendingRequestDataPromise = null;
        throw err;
      });

    return this._fetchingPendingRequestDataPromise;
  },

  populatePendingRequestsRecursively(): Promise<void> {
    function maybeFetchPendingRequestData(model): Promise<void> {
      if (
        model == null ||
        (typeof model.isEphemeral === "function" && model.isEphemeral())
      ) {
        return Promise.resolve();
      }

      let returnValue = Promise.resolve();

      if (
        typeof model.hasPendingRequestLink === "function" &&
        model.hasPendingRequestLink()
      ) {
        returnValue = model
          .fetchPendingRequestData()
          .then((data) => {
            model.set(data);
          })
          .catch(() => {
            // ignore
          });
      }

      const nestedCollectionKeys =
        typeof model.nestedCollections === "function" &&
        Object.keys(model.nestedCollections());
      if (!nestedCollectionKeys || nestedCollectionKeys.length === 0) {
        return returnValue;
      } else {
        return returnValue
          .then(() =>
            Promise.all(
              nestedCollectionKeys.map((collectionKey) => {
                const collection = model.get(collectionKey);
                if (collection == null || collection.length === 0) {
                  return null;
                }
                return Promise.all(
                  collection.map(maybeFetchPendingRequestData)
                );
              })
            )
          )
          .then(() => {
            /* discard response */
          });
      }
    }

    return maybeFetchPendingRequestData(this);
  },
};



// WEBPACK FOOTER //
// ./src/framework/resource.js